import React from "react";
import PropTypes from "prop-types";
import SelectChipContainer from "../../../../connectui/component/input/SelectChipContainer";
import BaseFieldComponent from "../BaseFieldComponent";
import {BOOKING_STATUS_COLORS} from "configuration/settings";
import {bookingStatusLabels} from "configuration/constants";
import Text from "../../../../connectui/component/display/Text";
import {getDefaultFieldLabel} from "../../../../utilities/helperFunctions";
import ChipText from "connectui/component/display/chip/ChipText";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {useTheme} from "connectui/theme/ThemeProvider";
import StatusIcon from "connectui/component/display/StatusIcon";

const bookingOptions = Object.entries(bookingStatusLabels).map(
  ([key, value]) => ({
    key,
    label: value,
  }),
);

const defaultColors = {
  main: BOOKING_STATUS_COLORS.default.main,
  text: BOOKING_STATUS_COLORS.default.text,
  bg: BOOKING_STATUS_COLORS.default.bg,
  bgHover: BOOKING_STATUS_COLORS.default.bgHover,
};

const BookingStatusField = ({fieldData, handleValueChange, ...props}) => {
  const {theme} = useTheme();
  const selectedOptions = Array.isArray(fieldData?.val?.[0])
    ? fieldData.val.flat()
    : fieldData?.val;

  const handleInputChange = (newSelection) => {
    handleValueChange(
      fieldData.type,
      Array.isArray(newSelection)
        ? newSelection.filter((f) => f !== "")
        : newSelection,
    );
  };

  const handleFilterChange = (newSelection) => {
    handleValueChange(
      "custom",
      !!newSelection.length
        ? [{path: fieldData.field, value: newSelection}]
        : [],
    );
  };

  const renderDisplay = (props = {}) => {
    const {option = fieldData?.val} = props;
    return (
      <ChipText
        text={
          <FlexContainer alignItems="center" gap={theme.spacing(2)}>
            <StatusIcon
              color={BOOKING_STATUS_COLORS[option]?.main ?? defaultColors.main}
              sx={{marginTop: theme.spacing(0.5)}}
            />
            <Text
              variant="textXS"
              weight="medium"
              sx={{
                color:
                  BOOKING_STATUS_COLORS[option]?.text ?? defaultColors.text,
                lineHeight: "21px",
              }}
            >
              {bookingOptions.find((o) => o.key === option)?.label || option}
            </Text>
          </FlexContainer>
        }
        sx={{
          height: "22px",
          backgroundColor:
            BOOKING_STATUS_COLORS[option]?.bg ?? defaultColors.bg,
          "&:hover": {
            backgroundColor:
              BOOKING_STATUS_COLORS[option]?.bg ?? defaultColors.bgHover,
          },
        }}
      />
    );
  };

  const renderInput = (isFilter) => (
    <SelectChipContainer
      options={bookingOptions.map((opt) => opt.key)}
      selectedOptions={selectedOptions}
      onChange={(newSelection) =>
        isFilter
          ? handleFilterChange(newSelection)
          : handleInputChange(newSelection)
      }
      multiSelect={true}
      sx={{
        getChipStyles: ({option, isSelected}) => ({
          backgroundColor: isSelected
            ? (BOOKING_STATUS_COLORS[option]?.bg ?? defaultColors.bg)
            : theme.colors.gray100,
          color: isSelected
            ? (BOOKING_STATUS_COLORS[option]?.text ?? defaultColors.text)
            : theme.colors.gray700,
          "&:hover": {
            backgroundColor: isSelected
              ? (BOOKING_STATUS_COLORS[option]?.bgHover ??
                defaultColors.bgHover)
              : theme.colors.gray200,
          },
        }),
      }}
      customRender={(option, isSelected) => (
        <FlexContainer alignItems="center" gap={theme.spacing(2)}>
          <StatusIcon
            color={BOOKING_STATUS_COLORS[option]?.main ?? defaultColors.main}
            sx={{marginTop: theme.spacing(0.5)}}
          />
          <Text
            variant="textSM"
            weight="medium"
            sx={{
              color: isSelected
                ? (BOOKING_STATUS_COLORS[option]?.text ?? defaultColors.text)
                : theme.colors.textPrimary,
            }}
          >
            {bookingOptions.find((o) => o.key === option)?.label || option}
          </Text>
        </FlexContainer>
      )}
    />
  );

  const getLabel = () => getDefaultFieldLabel(fieldData?.val);

  const getOperators = () => [
    "contains",
    "not_contains",
    "exists",
    "not_exists",
    "is_one_of",
  ];

  return (
    <BaseFieldComponent
      renderDisplay={() => renderDisplay()}
      renderInput={() => renderInput()}
      getLabel={getLabel}
      renderFilter={() => renderInput(true)}
      getOperators={getOperators}
      {...props}
    />
  );
};

BookingStatusField.propTypes = {
  fieldData: PropTypes.object.isRequired,
  handleValueChange: PropTypes.func,
};

export default BookingStatusField;
